import { Suspense, useEffect, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { DragDropGuard } from "@/components/custom/dragDropGuard";
import { AssistantRouteHistoryProvider } from "@/components/custom/contexts/assistantRouteHistoryContext";
import { LocaleProvider } from "@/components/custom/contexts/localeContext";
import { UserSettingsProvider } from "@/components/custom/contexts/userSettingsContext";
import { AuthProvider, useAuth } from "@/lib/auth/AuthContext";
import { ForbiddenActionDialog } from "@/components/custom/forbiddenActionDialog";
import { IdpUnavailableBanner } from "@/components/custom/idpUnavailableBanner";
import { PraetorRevisionToastBridge } from "@/components/custom/praetorRevisionToastBridge";
import { ApprovalSuccessToastBridge } from "@/components/custom/approvalSuccessToastBridge";
import { GlobalErrorBoundary } from "@/components/ui/global-error-boundary";
import LoginPage from "./routes/LoginPage";
import ChatsPage from "./routes/ChatsPage";
import CasePage from "./routes/CasePage";
import CaseThreadPage from "./routes/CaseThreadPage";
import SettingsAiPage from "./routes/SettingsAiPage";
import SettingsSkillsPage from "./routes/SettingsSkillsPage";
import SettingsSkillDetailPage from "./routes/SettingsSkillDetailPage";
import SettingsSkillCreatePage from "./routes/SettingsSkillCreatePage";
import SettingsTemplatesPage from "./routes/SettingsTemplatesPage";
import SettingsUserPage from "./routes/SettingsUserPage";
import SettingsDevToolsPage from "./routes/SettingsDevToolsPage";
import SettingsAdminSkillsPage from "./routes/SettingsAdminSkillsPage";
import SettingsAdminSkillCreatePage from "./routes/SettingsAdminSkillCreatePage";
import SettingsAdminSkillDetailPage from "./routes/SettingsAdminSkillDetailPage";
import SettingsAdminPreferencesPage from "./routes/SettingsAdminPreferencesPage";
import "@/lib/i18n";

// Reads the AuthContext probe (`/api/auth/whoami`). While the first probe is in
// flight we render nothing — the alternative is a flash of /login on cold load
// for an authenticated user. On confirmed 401 we redirect with returnUrl.
function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (status === "loading") return null;
  if (status === "anonymous") {
    const returnUrl = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }
  return <>{children}</>;
}

// One-shot boot cleanup: returning users have orphaned `_auth_mode` and
// `_auth_credentials` localStorage entries from the pre-BFF model. Wipe them.
// Safe to remove from the codebase ~3 months after deploy.
function cleanupOrphanedAuthStorage() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem("_auth_mode");
    localStorage.removeItem("_auth_credentials");
    // The forgeable session cookie is also gone — clear it on returning users
    // so DevTools doesn't misleadingly show a stale value.
    document.cookie = "_auth_session=; Path=/; Max-Age=0";
  } catch {
    // localStorage can throw in private mode / quota-exceeded — ignore.
  }
}

export default function App() {
  useEffect(() => {
    cleanupOrphanedAuthStorage();
  }, []);

  return (
    <BrowserRouter>
      <GlobalErrorBoundary>
        <DragDropGuard />
        <UserSettingsProvider>
          <Suspense fallback={null}>
            <LocaleProvider>
              <AssistantRouteHistoryProvider>
                <AuthProvider>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                      path="/"
                      element={
                        <RequireAuth>
                          <Navigate to="/default" replace />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/chats"
                      element={
                        <RequireAuth>
                          <ChatsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <RequireAuth>
                          <Navigate to="/settings/skills" replace />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/skills"
                      element={
                        <RequireAuth>
                          <SettingsSkillsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/skills/new"
                      element={
                        <RequireAuth>
                          <SettingsSkillCreatePage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/skills/:id"
                      element={
                        <RequireAuth>
                          <SettingsSkillDetailPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/ai"
                      element={
                        <RequireAuth>
                          <SettingsAiPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/templates"
                      element={
                        <RequireAuth>
                          <SettingsTemplatesPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/user"
                      element={
                        <RequireAuth>
                          <SettingsUserPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/dev-tools"
                      element={
                        <RequireAuth>
                          <SettingsDevToolsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/tenant"
                      element={
                        <RequireAuth>
                          <Navigate to="/settings/admin/preferences" replace />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/admin"
                      element={
                        <RequireAuth>
                          <Navigate to="/settings/admin/skills" replace />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/admin/skills"
                      element={
                        <RequireAuth>
                          <SettingsAdminSkillsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/admin/skills/new"
                      element={
                        <RequireAuth>
                          <SettingsAdminSkillCreatePage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/admin/skills/:id"
                      element={
                        <RequireAuth>
                          <SettingsAdminSkillDetailPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings/admin/preferences"
                      element={
                        <RequireAuth>
                          <SettingsAdminPreferencesPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/:caseId"
                      element={
                        <RequireAuth>
                          <CasePage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/:caseId/:threadId"
                      element={
                        <RequireAuth>
                          <CaseThreadPage />
                        </RequireAuth>
                      }
                    />
                  </Routes>
                  <ForbiddenActionDialog />
                  <IdpUnavailableBanner />
                  <PraetorRevisionToastBridge />
                  <ApprovalSuccessToastBridge />
                </AuthProvider>
              </AssistantRouteHistoryProvider>
            </LocaleProvider>
          </Suspense>
        </UserSettingsProvider>
        <Toaster position="top-center" richColors />
      </GlobalErrorBoundary>
    </BrowserRouter>
  );
}
